import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, Star } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "@/components/ui/sonner";

const pctColor = (p, th = 70) => (p == null ? "text-slate-400" : p < th ? "text-red-700" : "text-emerald-700");

export default function StudentDashboard() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [viewClass, setViewClass] = useState(null);
  const [feedbackFor, setFeedbackFor] = useState(null);
  const [fbRating, setFbRating] = useState(0);
  const [fbText, setFbText] = useState("");
  const [fbSaving, setFbSaving] = useState(false);
  const { data } = useQuery({
    queryKey: ["student-dashboard"],
    queryFn: async () => (await api.get("/student/dashboard")).data,
  });
  const { data: myFeedback = {} } = useQuery({
    queryKey: ["my-feedback"],
    queryFn: async () => (await api.get("/student/feedback")).data,
  });

  const openFeedback = (h) => {
    const existing = myFeedback[h.id];
    setFbRating(existing?.rating || 0);
    setFbText(existing?.text || "");
    setFeedbackFor(h);
  };

  const saveFeedback = async () => {
    if (!fbRating) {
      toast.error("Please pick a star rating");
      return;
    }
    setFbSaving(true);
    try {
      await api.post("/student/feedback", { attendance_id: feedbackFor.id, rating: fbRating, text: fbText });
      toast.success("Feedback saved");
      setFeedbackFor(null);
      queryClient.invalidateQueries({ queryKey: ["my-feedback"] });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setFbSaving(false);
    }
  };

  const overall = data?.overall_percentage;
  const th = data?.threshold ?? 70;

  return (
    <Layout title="My Attendance">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="bg-slate-950 text-white rounded-md p-8" data-testid="overall-card">
          <p className="text-sm text-slate-400">Overall attendance</p>
          <p className={`font-data text-5xl font-bold mt-4 ${overall != null && overall < th ? "text-red-400" : ""}`}
            data-testid="overall-percentage">
            {overall != null ? `${overall}%` : "—"}
          </p>
          <p className="text-sm text-slate-400 mt-4 font-data" data-testid="overall-fraction">
            {data ? `${data.total_present} / ${data.total_classes} classes attended` : ""}
          </p>
          <p className="text-xs text-slate-500 mt-6" data-testid="student-context">
            {user?.name} · {data?.department_name} · {data?.semester_name}
          </p>
          {overall != null && overall < th && (
            <p className="mt-4 text-xs text-red-300 bg-red-950/60 border border-red-900 rounded-md px-3 py-2" data-testid="overall-warning">
              Below the {th}% requirement. Attend upcoming classes to catch up.
            </p>
          )}
        </div>

        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-md p-6" data-testid="per-subject-card">
          <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950 mb-6">Per-subject attendance</h2>
          {data?.per_subject?.length === 0 && (
            <p className="text-sm text-slate-400" data-testid="per-subject-empty">No subjects assigned to your semester yet.</p>
          )}
          <div className="space-y-5">
            {data?.per_subject?.map((s) => (
              <div key={s.subject_id} data-testid={`subject-row-${s.subject_id}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-sm font-medium text-slate-950">{s.name} <span className="text-slate-400 font-data text-xs">{s.code}</span></p>
                  <p className={`font-data text-sm font-semibold ${pctColor(s.percentage, th)}`}
                    data-testid={`subject-pct-${s.subject_id}`}>
                    {s.percentage != null ? `${s.percentage}%` : "No classes yet"}
                    {s.percentage != null && <span className="text-slate-400 font-normal ml-2">{s.present}/{s.total}</span>}
                  </p>
                </div>
                <Progress value={s.percentage ?? 0} className="h-2" />
                {s.percentage != null && s.percentage < th && (
                  <p className="text-xs text-red-700 mt-1">Below {th}% threshold</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md mt-8" data-testid="history-card">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950">Daily class history</h2>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Period</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>Marked by</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-24">Topic</TableHead>
              <TableHead className="w-28">Feedback</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(!data || data.history.length === 0) && (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10" data-testid="history-empty">
                No attendance records yet.
              </TableCell></TableRow>
            )}
            {data?.history?.map((h) => (
              <TableRow key={h.id} data-testid={`history-row-${h.id}`}>
                <TableCell className="font-data">{h.date}</TableCell>
                <TableCell>{h.period_label}</TableCell>
                <TableCell>{h.subject_name}</TableCell>
                <TableCell className="text-slate-500">{h.marked_by_name}</TableCell>
                <TableCell>
                  <Badge variant="outline" data-testid={`history-status-${h.id}`}
                    className={h.status === "present"
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-red-50 text-red-700 border-red-200"}>
                    {h.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Button variant="outline" size="sm" data-testid={`view-topic-${h.id}`}
                    onClick={() => setViewClass(h)} disabled={!h.topic}>
                    <Eye className="h-4 w-4" /> View
                  </Button>
                </TableCell>
                <TableCell>
                  <Button variant={myFeedback[h.id] ? "outline" : "ghost"} size="sm"
                    data-testid={`feedback-btn-${h.id}`} onClick={() => openFeedback(h)}
                    disabled={h.status !== "present"}
                    title={h.status !== "present" ? "Feedback is allowed only for classes you attended" : undefined}
                    className={myFeedback[h.id] ? "text-amber-600 border-amber-300" : "text-slate-500"}>
                    <Star className={`h-4 w-4 ${myFeedback[h.id] ? "fill-amber-400 text-amber-400" : ""}`} />
                    {myFeedback[h.id] ? `${myFeedback[h.id].rating}/5` : "Rate"}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!feedbackFor} onOpenChange={() => setFeedbackFor(null)}>
        <DialogContent className="bg-white rounded-md" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle>Feedback — {feedbackFor?.subject_name} · {feedbackFor?.date}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-slate-500 mb-2">How was this class?</p>
              <div className="flex gap-1.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" data-testid={`star-${n}`}
                    onClick={() => setFbRating(n)}
                    className="active:scale-90 transition-transform duration-150">
                    <Star className={`h-8 w-8 ${fbRating >= n ? "fill-amber-400 text-amber-400" : "text-slate-300 hover:text-amber-300"}`} />
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <p className="text-sm text-slate-500">Comments (optional)</p>
              <Textarea rows={3} data-testid="feedback-text-input" placeholder="What went well? What could improve?"
                value={fbText} onChange={(e) => setFbText(e.target.value)} />
            </div>
            <Button onClick={saveFeedback} disabled={fbSaving} data-testid="feedback-submit-btn"
              className="w-full bg-slate-950 hover:bg-slate-800 text-white">
              {fbSaving ? "Saving…" : "Save feedback"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewClass} onOpenChange={() => setViewClass(null)}>
        <DialogContent className="bg-white rounded-md" data-testid="topic-dialog">
          <DialogHeader>
            <DialogTitle>{viewClass?.subject_name} — {viewClass?.date} · {viewClass?.period_label}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <span>Marked by {viewClass?.marked_by_name}</span>
              <Badge variant="outline" className={viewClass?.status === "present"
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-red-50 text-red-700 border-red-200"}>
                {viewClass?.status}
              </Badge>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-md px-4 py-3" data-testid="topic-dialog-body">
              <p className="text-xs text-slate-400 mb-1">Topic taught</p>
              <p className="text-sm text-slate-800">{viewClass?.topic}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
